import {
  useState,
} from 'react';
import axios from 'axios';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  FolderOpenRounded,
  RefreshRounded,
  WorkOutlineRounded,
} from '@mui/icons-material';

import {
  createStacManagedProject,
  listStacManagedProjects,
  listStacManagedTasks,
  updateStacManagedTaskProject,
} from '../services/stacService';


function getApiErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }

  const detail =
    error.response?.data?.detail;

  return (
    typeof detail === 'string'
    && detail.trim()
  )
    ? detail
    : fallback;
}


export default function StacProjectsView() {
  const queryClient =
    useQueryClient();

  const {
    enqueueSnackbar,
  } = useSnackbar();

  const [
    projectName,
    setProjectName,
  ] = useState('');

  const [
    projectDescription,
    setProjectDescription,
  ] = useState('');

  const projectsQuery = useQuery({
    queryKey: [
      'stac-managed-projects',
    ],
    queryFn:
      listStacManagedProjects,
    retry: false,
  });

  const tasksQuery = useQuery({
    queryKey: [
      'stac-managed-tasks',
    ],
    queryFn:
      listStacManagedTasks,
    retry: false,
  });

  const projects =
    projectsQuery.data ?? [];

  const tasks =
    tasksQuery.data ?? [];

  const refreshProjects =
    async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            'stac-managed-projects',
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'stac-managed-tasks',
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'stac-tasks',
          ],
        }),
      ]);
    };

  const createProjectMutation =
    useMutation({
      mutationFn:
        createStacManagedProject,

      onSuccess: async (project) => {
        setProjectName('');
        setProjectDescription('');

        enqueueSnackbar(
          `Proyecto ${project.name} creado.`,
          {
            variant: 'success',
          },
        );

        await refreshProjects();
      },

      onError: (error) => {
        enqueueSnackbar(
          getApiErrorMessage(
            error,
            'No se pudo crear el proyecto.',
          ),
          {
            variant: 'error',
          },
        );
      },
    });

  const updateTaskProjectMutation =
    useMutation({
      mutationFn: ({
        taskId,
        projectId,
      }: {
        taskId: string;
        projectId: string | null;
      }) =>
        updateStacManagedTaskProject(
          taskId,
          {
            project_id:
              projectId,
          },
        ),

      onSuccess: async () => {
        enqueueSnackbar(
          'Proyecto de la tarea actualizado.',
          {
            variant: 'success',
          },
        );

        await refreshProjects();
      },

      onError: (error) => {
        enqueueSnackbar(
          getApiErrorMessage(
            error,
            'No se pudo actualizar el proyecto de la tarea.',
          ),
          {
            variant: 'error',
          },
        );
      },
    });

  const handleCreateProject = () => {
    const name =
      projectName.trim();

    if (!name) {
      return;
    }

    createProjectMutation.mutate({
      name,
      description:
        projectDescription.trim()
        || null,
    });
  };

  if (
    projectsQuery.isLoading
    || tasksQuery.isLoading
  ) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border:
            '1px solid rgba(148,163,184,0.14)',
          bgcolor:
            'rgba(15,23,42,0.72)',
          p: 2.5,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <CircularProgress
            size={20}
          />

          <Typography
            sx={{
              color: '#94a3b8',
            }}
          >
            Cargando proyectos...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (
    projectsQuery.isError
    || tasksQuery.isError
  ) {
    return (
      <Alert
        severity="error"
        variant="outlined"
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={
              <RefreshRounded />
            }
            onClick={() => {
              void Promise.all([
                projectsQuery.refetch(),
                tasksQuery.refetch(),
              ]);
            }}
          >
            Reintentar
          </Button>
        }
      >
        No se pudo cargar la gestión
        de proyectos STAC.
      </Alert>
    );
  }

  const unclassifiedTasks =
    tasks.filter(
      (task) =>
        !task.project_id
    );

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border:
            '1px solid rgba(148,163,184,0.16)',
          bgcolor:
            'rgba(15,23,42,0.72)',
          p: {
            xs: 2,
            md: 2.5,
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs: 'stretch',
            md: 'center',
          }}
          spacing={1.5}
        >
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <FolderOpenRounded
                sx={{
                  color: '#38bdf8',
                }}
              />

              <Typography
                sx={{
                  color: '#f8fafc',
                  fontSize: '1.05rem',
                  fontWeight: 900,
                }}
              >
                Proyectos
              </Typography>
            </Stack>

            <Typography
              sx={{
                mt: 0.4,
                color: '#94a3b8',
                fontSize: '0.86rem',
              }}
            >
              Agrupa trabajo relacionado sin
              obligar a que todas las tareas
              pertenezcan a un proyecto.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={
              <RefreshRounded />
            }
            disabled={
              projectsQuery.isFetching
              || tasksQuery.isFetching
            }
            onClick={() => {
              void refreshProjects();
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Actualizar
          </Button>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg:
              'minmax(280px, 0.7fr) '
              + 'minmax(0, 1.3fr)',
          },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border:
              '1px solid rgba(148,163,184,0.14)',
            bgcolor:
              'rgba(15,23,42,0.72)',
            p: 2.5,
          }}
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <AddRounded
                sx={{
                  color: '#38bdf8',
                }}
              />

              <Box>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                  }}
                >
                  Crear proyecto
                </Typography>

                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.78rem',
                  }}
                >
                  Solo nombre y descripción
                  opcional en esta primera versión.
                </Typography>
              </Box>
            </Stack>

            <TextField
              label="Nombre"
              value={projectName}
              onChange={(event) =>
                setProjectName(
                  event.target.value
                )
              }
              inputProps={{
                maxLength: 240,
              }}
              fullWidth
            />

            <TextField
              label="Descripción"
              value={projectDescription}
              onChange={(event) =>
                setProjectDescription(
                  event.target.value
                )
              }
              multiline
              minRows={3}
              inputProps={{
                maxLength: 2000,
              }}
              fullWidth
            />

            <Button
              variant="contained"
              startIcon={
                <AddRounded />
              }
              disabled={
                createProjectMutation
                  .isPending
                || !projectName.trim()
              }
              onClick={
                handleCreateProject
              }
              sx={{
                alignSelf: 'flex-start',
                textTransform: 'none',
                fontWeight: 900,
              }}
            >
              {createProjectMutation
                .isPending
                ? 'Creando...'
                : 'Crear proyecto'}
            </Button>
          </Stack>
        </Paper>

        <Stack spacing={2}>
          {projects.length ? (
            projects.map(
              (project) => {
                const projectTasks =
                  tasks.filter(
                    (task) =>
                      task.project_id
                      === project.id
                  );

                return (
                  <Paper
                    key={project.id}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border:
                        '1px solid rgba(148,163,184,0.14)',
                      bgcolor:
                        'rgba(15,23,42,0.72)',
                      p: 2.5,
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Box>
                        <Stack
                          direction={{
                            xs: 'column',
                            sm: 'row',
                          }}
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box>
                            <Typography
                              sx={{
                                color:
                                  '#f8fafc',
                                fontWeight:
                                  900,
                                fontSize:
                                  '1.02rem',
                              }}
                            >
                              {project.name}
                            </Typography>

                            {project.description ? (
                              <Typography
                                sx={{
                                  mt: 0.35,
                                  color:
                                    '#94a3b8',
                                  fontSize:
                                    '0.84rem',
                                  whiteSpace:
                                    'pre-wrap',
                                }}
                              >
                                {
                                  project.description
                                }
                              </Typography>
                            ) : null}
                          </Box>

                          <Chip
                            size="small"
                            label={`${projectTasks.length} ${
                              projectTasks.length === 1
                                ? 'tarea'
                                : 'tareas'
                            }`}
                            variant="outlined"
                          />
                        </Stack>
                      </Box>

                      <Divider />

                      {projectTasks.length ? (
                        <Stack spacing={1}>
                          {projectTasks.map(
                            (task) => (
                              <Paper
                                key={task.id}
                                elevation={0}
                                sx={{
                                  borderRadius: 2.5,
                                  border:
                                    '1px solid rgba(148,163,184,0.10)',
                                  bgcolor:
                                    'rgba(2,6,23,0.28)',
                                  p: 1.4,
                                }}
                              >
                                <Stack
                                  direction={{
                                    xs: 'column',
                                    md: 'row',
                                  }}
                                  justifyContent="space-between"
                                  alignItems={{
                                    xs: 'stretch',
                                    md: 'center',
                                  }}
                                  spacing={1}
                                >
                                  <Box>
                                    <Typography
                                      sx={{
                                        color:
                                          '#f8fafc',
                                        fontWeight:
                                          850,
                                      }}
                                    >
                                      {task.title}
                                    </Typography>

                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      useFlexGap
                                      flexWrap="wrap"
                                      sx={{
                                        mt: 0.6,
                                      }}
                                    >
                                      {task.assignments.map(
                                        (
                                          assignment
                                        ) => (
                                          <Chip
                                            key={
                                              assignment
                                                .worker_id
                                            }
                                            size="small"
                                            label={
                                              assignment
                                                .display_name
                                            }
                                            variant="outlined"
                                          />
                                        ),
                                      )}
                                    </Stack>
                                  </Box>

                                  <TextField
                                    select
                                    size="small"
                                    label="Proyecto"
                                    value={
                                      task.project_id
                                      ?? ''
                                    }
                                    onChange={(
                                      event
                                    ) =>
                                      updateTaskProjectMutation
                                        .mutate({
                                          taskId:
                                            task.id,
                                          projectId:
                                            event
                                              .target
                                              .value
                                            || null,
                                        })
                                    }
                                    disabled={
                                      updateTaskProjectMutation
                                        .isPending
                                    }
                                    sx={{
                                      minWidth: {
                                        xs: '100%',
                                        md: 220,
                                      },
                                    }}
                                  >
                                    <MenuItem value="">
                                      Sin proyecto
                                    </MenuItem>

                                    {projects.map(
                                      (
                                        option
                                      ) => (
                                        <MenuItem
                                          key={
                                            option.id
                                          }
                                          value={
                                            option.id
                                          }
                                        >
                                          {
                                            option.name
                                          }
                                        </MenuItem>
                                      ),
                                    )}
                                  </TextField>
                                </Stack>
                              </Paper>
                            ),
                          )}
                        </Stack>
                      ) : (
                        <Typography
                          sx={{
                            color: '#94a3b8',
                            fontSize: '0.86rem',
                          }}
                        >
                          Todavía no hay tareas
                          clasificadas en este proyecto.
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                );
              },
            )
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border:
                  '1px solid rgba(148,163,184,0.14)',
                bgcolor:
                  'rgba(15,23,42,0.72)',
                p: 2.5,
              }}
            >
              <Typography
                sx={{
                  color: '#94a3b8',
                }}
              >
                Todavía no has creado proyectos.
              </Typography>
            </Paper>
          )}

          {unclassifiedTasks.length ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border:
                  '1px solid rgba(148,163,184,0.14)',
                bgcolor:
                  'rgba(15,23,42,0.72)',
                p: 2.5,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <WorkOutlineRounded
                    sx={{
                      color: '#94a3b8',
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 900,
                      }}
                    >
                      Sin proyecto
                    </Typography>

                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.78rem',
                      }}
                    >
                      Trabajo que todavía no
                      necesita o no tiene clasificación.
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={1}>
                  {unclassifiedTasks.map(
                    (task) => (
                      <Paper
                        key={task.id}
                        elevation={0}
                        sx={{
                          borderRadius: 2.5,
                          border:
                            '1px solid rgba(148,163,184,0.10)',
                          bgcolor:
                            'rgba(2,6,23,0.28)',
                          p: 1.4,
                        }}
                      >
                        <Stack
                          direction={{
                            xs: 'column',
                            md: 'row',
                          }}
                          justifyContent="space-between"
                          alignItems={{
                            xs: 'stretch',
                            md: 'center',
                          }}
                          spacing={1}
                        >
                          <Typography
                            sx={{
                              color:
                                '#f8fafc',
                              fontWeight:
                                850,
                            }}
                          >
                            {task.title}
                          </Typography>

                          <TextField
                            select
                            size="small"
                            label="Proyecto"
                            value=""
                            onChange={(
                              event
                            ) =>
                              updateTaskProjectMutation
                                .mutate({
                                  taskId:
                                    task.id,
                                  projectId:
                                    event.target
                                      .value
                                    || null,
                                })
                            }
                            disabled={
                              updateTaskProjectMutation
                                .isPending
                            }
                            sx={{
                              minWidth: {
                                xs: '100%',
                                md: 220,
                              },
                            }}
                          >
                            <MenuItem value="">
                              Sin proyecto
                            </MenuItem>

                            {projects.map(
                              (
                                project
                              ) => (
                                <MenuItem
                                  key={
                                    project.id
                                  }
                                  value={
                                    project.id
                                  }
                                >
                                  {
                                    project.name
                                  }
                                </MenuItem>
                              ),
                            )}
                          </TextField>
                        </Stack>
                      </Paper>
                    ),
                  )}
                </Stack>
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
}